import { Component, computed, inject, resource } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { DatePipe, formatCurrency } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ButtonComponent, ModalService, ModalType, SnackbarComponent, ToastType } from '@program-suite/ui';
import { InspectionStatusEnum, RCIPReportOperations, TypeOfWorkPercentatges } from '@program-suite/data-common/program';
import { InspectionsService } from '../data/inspections.service';
import { STATUS_LABELS } from '../data/inspection-status-labels';
import { ManageInspectionModalComponent } from '../manage-inspection-modal/manage-inspection-modal.component';

interface ComparisonRow {
  label: string;
  application: string;
  inspection: string;
  changed: boolean;
}

const EMPTY = '—';

const money = (value: number | null | undefined) => (value == null ? null : formatCurrency(value, 'en-US', '$', 'USD', '1.0-0'));
const yesNo = (value: boolean | null | undefined) => (value == null ? null : value ? 'Yes' : 'No');
const describeClass = (c: TypeOfWorkPercentatges | undefined) =>
  c ? [c.percentage != null ? `${c.percentage}%` : null, money(c.payroll)].filter(Boolean).join(' · ') : null;

// A missing application value (a legacy policy's owners) was never asked, so it isn't a change.
function compare(label: string, application: string | number | null | undefined, inspection: string | number | null | undefined): ComparisonRow {
  return {
    label,
    application: application?.toString() ?? EMPTY,
    inspection: inspection?.toString() ?? EMPTY,
    changed: application != null && application !== inspection,
  };
}

@Component({
  selector: 'lib-inspection-report',
  imports: [ButtonComponent, DatePipe],
  templateUrl: './inspection-report.component.html',
  styleUrl: './inspection-report.component.scss',
})
export class InspectionReportComponent {
  inspectionsService = inject(InspectionsService);
  snackbar = inject(MatSnackBar);
  modalService = inject(ModalService);
  router = inject(Router);
  // Re-issue and order navigate to the new inspection on this same route, so the id is read live.
  inspectionGuid = toSignal(inject(ActivatedRoute).paramMap.pipe(map(params => params.get('id')!)), { requireSync: true });

  inspection = resource({
    params: () => this.inspectionGuid(),
    loader: ({ params }) => this.inspectionsService.getInspection(params),
  });
  status = computed(() => (this.inspection.hasValue() ? STATUS_LABELS[this.inspection.value().status!] : undefined));
  // Only a completed inspection has a report.
  report = resource({
    params: () => (this.inspection.value()?.status === InspectionStatusEnum.Complete ? this.inspectionGuid() : undefined),
    loader: ({ params }) => this.inspectionsService.getInspectionReport(params),
  });
  // value() throws once the resource has errored, so everything reads the report through this.
  model = computed(() => (this.report.hasValue() ? this.report.value() : undefined));

  businessType = computed(() => this.model()?.insuredBusinessInformation?.businessType?.replace(/([a-z])([A-Z])/g, '$1 $2'));

  operations = computed<ComparisonRow[]>(() => {
    const report = this.model();
    if (!report) {
      return [];
    }
    const application: RCIPReportOperations = report.application ?? {};
    const inspection: RCIPReportOperations = report.inspection ?? {};
    return [
      compare('Description of Operations', application.descriptionOfOperations, inspection.descriptionOfOperations),
      compare('Annual Gross Receipts', money(application.grossReceipts), money(inspection.grossReceipts)),
      compare(report.legacyPolicy ? 'Annual Payroll' : 'Annual Employee Payroll', money(application.annualPayroll), money(inspection.annualPayroll)),
      compare('Subcontracted Costs', money(application.subcontractedCosts), money(inspection.subcontractedCosts)),
      compare('Owners in the Field', application.numberOfOwnersInField, inspection.numberOfOwnersInField),
      compare('Number of Employees', application.numberOfEmployees, inspection.numberOfEmployees),
      compare('Performs New Construction', yesNo(application.performNewConstruction), yesNo(inspection.performNewConstruction)),
      compare('Uses Day Labor', yesNo(application.usesDayLabor), yesNo(inspection.usesDayLabor)),
    ];
  });

  // Matched by name so a class the insured added or dropped shows on its own row.
  classes = computed<ComparisonRow[]>(() => {
    const report = this.model();
    const application = report?.application?.typeOfWorkPercentages ?? [];
    const inspection = report?.inspection?.typeOfWorkPercentages ?? [];
    const names = [...new Set([...application, ...inspection].map(c => c.typeOfWork ?? ''))];
    return names.map(name => {
      const a = describeClass(application.find(c => c.typeOfWork === name));
      const i = describeClass(inspection.find(c => c.typeOfWork === name));
      return { ...compare(name, a, i), changed: a !== i };
    });
  });

  manage() {
    this.modalService.open(ManageInspectionModalComponent, { modalType: ModalType.Large, data: this.inspection.value() }, (inspectionGuid?: string) => {
      if (inspectionGuid === this.inspectionGuid()) {
        this.inspection.reload();
      } else if (inspectionGuid) {
        this.router.navigate(['/inspections', inspectionGuid]);
      }
    });
  }

  async download() {
    try {
      await this.inspectionsService.downloadInspectionReport(this.inspectionGuid());
    } catch (error: any) {
      this.snackbar.openFromComponent(SnackbarComponent, {
        duration: 5000,
        data: { toastType: ToastType.Error, message: error.message },
      });
    }
  }
}
